import authConfig from "../../config/auth.config.json";
import bancoConfig from "../../config/banco.config.json";
import {
  setLocalItem,
  setSessionItem,
  removeLocalItem,
  removeSessionItem,
} from "@/lib/storage-safe";

export async function obterClienteDados(): Promise<any> {
  // Propaga configuração de logging de sentenças SQL para o runtime
  try {
    const g: any = typeof window === "undefined" ? globalThis : (window as any);
  } catch {
    /* ignore */
  }
  // Server: return the server-side client that may execute directly on Postgres.
  if (typeof window === "undefined") {
    const { dadosServidor } = await import("@/lib/dados.server");
    return await dadosServidor(null);
  }
  // Browser: try to import the ponte (bridge) and the supa-base shim. If any
  // import fails, return a safe stub implementation so call sites won't blow
  // up with "cannot read properties of undefined (reading 'from')".
  let ponte: any = null;
  let clienteDados: any = null;
  try {
    ponte = await import("@/lib/ponte-banco-externo");
  } catch {
    ponte = null;
  }
  // Do not attempt to import any cloud client. Use the bridge or stubs.
  clienteDados = null;

  // Create a chainable stub query object that mimics the external ConsultaExterna
  // shape enough for client code to call chained methods without throwing.
  const makeStubQuery = () => {
    const stub: any = {
      _error: { message: "data bridge unavailable" },
      select() {
        return this;
      },
      insert() {
        return this;
      },
      upsert() {
        return this;
      },
      update() {
        return this;
      },
      delete() {
        return this;
      },
      eq() {
        return this;
      },
      neq() {
        return this;
      },
      gt() {
        return this;
      },
      gte() {
        return this;
      },
      lt() {
        return this;
      },
      lte() {
        return this;
      },
      like() {
        return this;
      },
      ilike() {
        return this;
      },
      in() {
        return this;
      },
      contains() {
        return this;
      },
      is() {
        return this;
      },
      not() {
        return this;
      },
      or() {
        return this;
      },
      order() {
        return this;
      },
      limit() {
        return this;
      },
      range() {
        return this;
      },
      single() {
        return this;
      },
      maybeSingle() {
        return this;
      },
      returns() {
        return this;
      },
      abortSignal() {
        return this;
      },
      throwOnError() {
        return this;
      },
      then(resolve: any) {
        // Resolve to an error-shaped response so callers get a predictable result.
        const resp = {
          data: null,
          error: this._error,
          count: null,
          status: 500,
          statusText: "Error",
        };
        if (typeof resolve === "function") return Promise.resolve(resolve(resp));
        return Promise.resolve(resp);
      },
    } as any;
    return stub;
  };

  const client: any = {
    from: (t: string) => (ponte?.fromExterno ? ponte.fromExterno(t) : makeStubQuery()),
    rpc: (n: string, a?: Record<string, unknown>) =>
      ponte?.rpcExterno
        ? ponte.rpcExterno(n, a)
        : Promise.resolve({ data: null, error: { message: "no rpc" } }),
    auth: (() => {
      // Browser-local auth shim stored at window.__OBTER_DADOS_AUTH.
      if (typeof window === "undefined") {
        return {
          getUser: async () => ({ data: { user: null }, error: null }),
          getSession: async () => ({ data: { session: null }, error: null }),
          onAuthStateChange: (_: any) => ({ data: null }),
          signOut: async () => ({ error: { message: "no auth on server" } }),
          signInWithPassword: async () => ({ error: { message: "no auth on server" } }),
          updateUser: async () => ({ error: { message: "no auth on server" } }),
        };
      }

      const globalKey = "__OBTER_DADOS_AUTH";
      const g: any =
        (window as any)[globalKey] ||
        ((window as any)[globalKey] = {
          user: null as any,
          session: null as any,
          listeners: new Set<any>(),
        });

      // Reidrata a sessão a partir do armazenamento (`auth-externo-sessao`) após
      // uma recarga completa de página — por exemplo, no retorno do login com
      // Google, que grava a sessão e faz `window.location`. Sem isto, `g.user`
      // ficaria nulo e o guard de rotas mandaria de volta para o login.
      if (!g.user && !(g as any).__hydratedFromStorage) {
        (g as any).__hydratedFromStorage = true;
        try {
          import("@/lib/storage-safe")
            .then(({ getSessionItem, getLocalItem }) => {
              try {
                const texto =
                  getSessionItem("auth-externo-sessao") ?? getLocalItem("auth-externo-sessao");
                if (texto) {
                  const guardada: any = JSON.parse(texto);
                  const bruto = guardada?.user ?? guardada?.usuario ?? null;
                  if (guardada?.access_token && bruto?.id) {
                    const usuario = {
                      id: bruto.id,
                      email: bruto.email,
                      user_metadata: bruto.user_metadata ?? {
                        ...(bruto.metadados ?? {}),
                        nome: bruto.nome,
                        nivel: bruto.nivel,
                      },
                    };
                    g.user = usuario;
                    g.session = { access_token: guardada.access_token, user: usuario };
                  }
                }
              } catch {
                /* ignore JSON errors */
              }
            })
            .catch(() => {
              /* ignore import failures */
            });
        } catch {
          /* armazenamento indisponível ou JSON inválido: ignora */
        }
      }

      // If bypassAuth is enabled in config, pre-populate a fake logged-in session
      // so the app runs without showing the login form. Do this only once per
      // page load to avoid repeatedly notifying listeners and triggering
      // continuous re-renders.
      if (authConfig?.bypassAuth && !(g as any).__bypassInjected) {
        const fakeUser = {
          id: "a6ed73cd-ec04-4f84-8e49-d70180db1052",
          email: "ricardo.rr@gmail.com",
          user_metadata: { nivel: 1 },
        };
        const fakeSession = { access_token: `bypass-token`, user: fakeUser };
        g.user = fakeUser;
        g.session = fakeSession;
        (g as any).__bypassInjected = true;
        // Notify any listeners (e.g., useAuth subscription)
        for (const l of Array.from(g.listeners) as any[]) {
          try {
            l("INITIAL_SESSION", fakeSession);
          } catch {
            /* swallow */
          }
        }
      }

      const notify = (event: string, session: any) => {
        for (const l of Array.from(g.listeners) as any[]) {
          try {
            l(event, session);
          } catch {
            /* swallow */
          }
        }
      };

      return {
        getUser: async () => {
          // Recuperação automática: com o banco EXTERNO, revalida o token no
          // servidor. Só limpa a sessão quando o servidor CONFIRMA que o
          // usuário não existe mais (token órfão). Falhas de transporte —
          // servidor ou banco reiniciando — mantêm a sessão para recuperar
          // sozinho quando voltarem.
          const usarExterno =
            String((bancoConfig as any)?.usar ?? "cloud").toLowerCase() === "externo" &&
            String((bancoConfig as any)?.externo?.host ?? "").trim() !== "";
          const token: string | undefined = g.session?.access_token;
          if (!usarExterno || !token || token === "bypass-token") {
            return { data: { user: g.user ?? null }, error: null };
          }
          const agora = Date.now();
          // Só bloqueia na 1ª verificação da carga; depois recupera em 2º plano
          // (evita travar as rotas enquanto o servidor/banco está reiniciando).
          const jaTentou = !!g.__ultimaTentativaAuth;
          // Nova tentativa no máximo a cada 15s (conte sucesso E falha).
          if (!g.__revalidandoAuth && agora - (g.__ultimaTentativaAuth ?? 0) > 15000) {
            g.__ultimaTentativaAuth = agora;
            g.__revalidandoAuth = (async () => {
              try {
                const { usuarioAtualExterno } = await import("@/lib/auth-externo.functions");
                const resp: any = await usuarioAtualExterno({ data: { token } });
                if (resp && !resp.usuario) {
                  // Servidor respondeu e confirmou ausência → token órfão.
                  g.user = null;
                  g.session = null;
                  try {
                    removeSessionItem("auth-externo-sessao");
                    removeLocalItem("auth-externo-sessao");
                  } catch {
                    /* ignore storage errors */
                  }
                  notify("SIGNED_OUT", null);
                } else if (resp && resp.usuario) {
                  const u = resp.usuario;
                  g.user = {
                    id: u.id,
                    email: u.email,
                    user_metadata: { ...(u.metadados ?? {}), nome: u.nome, nivel: u.nivel },
                  };
                  if (g.session) g.session.user = g.user;
                }
                // resp anormal (sem objeto): não altera nada (mantém sessão).
              } catch {
                // Servidor/banco reiniciando ou sem rede: mantém a sessão;
                // uma tentativa futura recupera automaticamente.
              } finally {
                g.__revalidandoAuth = null;
              }
            })();
          }
          // Aguarda apenas a primeira verificação da carga (a que o guard usa).
          if (g.__revalidandoAuth && !jaTentou) await g.__revalidandoAuth;
          return { data: { user: g.user ?? null }, error: null };
        },
        getSession: async () => ({ data: { session: g.session ?? null }, error: null }),
        onAuthStateChange: (cb: any) => {
          g.listeners.add(cb);
          return { data: { subscription: { unsubscribe: () => g.listeners.delete(cb) } } };
        },
        signOut: async (_opts?: any) => {
          g.user = null;
          g.session = null;
          (g as any).__hydratedFromStorage = true;
          try {
            removeSessionItem("auth-externo-sessao");
            removeLocalItem("auth-externo-sessao");
          } catch {
            /* ignore storage errors */
          }
          notify("SIGNED_OUT", null);
          return { error: null };
        },
        signInWithPassword: async (creds: any) => {
          // If the app is configured to use an external Postgres, call the
          // server function that validates credentials against public.users
          // and returns the signed token. This avoids a race with
          // instalarAuthExterno and ensures the correct token is stored.
          const usarExterno =
            String((bancoConfig as any)?.usar ?? "cloud").toLowerCase() === "externo" &&
            String((bancoConfig as any)?.externo?.host ?? "").trim() !== "";
          if (usarExterno) {
            try {
              const { entrarComSenhaExterna } = await import("@/lib/auth-externo.functions");
              const r: any = await entrarComSenhaExterna({
                data: { email: creds?.email, senha: creds?.password },
              });
              if (!r || !r.sessao) {
                return {
                  data: { session: null, user: null },
                  error: { message: r?.erro ?? "Falha no login" },
                };
              }
              const sessao = r.sessao;
              const usuario = {
                id: sessao.usuario.id,
                email: sessao.usuario.email,
                user_metadata: { ...(sessao.usuario.metadados ?? {}), nivel: sessao.usuario.nivel },
              };
              const sessionObj = { access_token: sessao.access_token, user: usuario };
              g.user = usuario;
              g.session = sessionObj;
              // Persist the external session under the same key used by
              // auth-externo (`auth-externo-sessao`) so other modules find it.
              try {
                const chave = "auth-externo-sessao";
                const texto = JSON.stringify({
                  access_token: sessao.access_token,
                  expires_at: sessao.expires_at,
                  usuario: sessao.usuario,
                });
                setSessionItem(chave, texto);
                setLocalItem(chave, texto);
              } catch {
                /* ignore storage errors */
              }
              notify("SIGNED_IN", sessionObj);
              return { data: { session: sessionObj, user: usuario }, error: null };
            } catch (e) {
              return {
                data: { session: null, user: null },
                error: { message: (e as Error).message ?? String(e) },
              };
            }
          }
          const user = {
            id: `local-${Date.now()}`,
            email: creds?.email ?? null,
            aud: "authenticated",
          };
          const session = { access_token: `local-token-${Date.now()}`, user };
          g.user = user;
          g.session = session;
          notify("SIGNED_IN", session);
          return { data: { user }, error: null };
        },
        updateUser: async (u: any) => {
          if (!g.user) return { data: null, error: { message: "not_logged_in" } };
          g.user = { ...g.user, ...u };
          if (g.session) g.session.user = g.user;
          notify("USER_UPDATED", g.session ?? null);
          return { data: { user: g.user }, error: null };
        },
      };
    })(),
    storage: clienteDados?.storage
      ? {
          from: (b: string) => clienteDados.storage.from(b),
        }
      : {
          from: (_: string) => ({
            createSignedUrl: async () => ({ error: { message: "no storage" } }),
            upload: async () => ({ error: { message: "no storage" } }),
          }),
        },
  };

  // Provide a `rest` surface so callers that expect `client.rest.from` work
  // even when the client only exposes top-level `from`/`rpc`.
  try {
    client.rest = {
      from: client.from.bind ? client.from.bind(client) : (t: string) => client.from(t),
      rpc: client.rpc.bind ? client.rpc.bind(client) : (n: string, a?: any) => client.rpc(n, a),
    };
  } catch {
    // Ignore if binding fails on some proxies; the top-level `from`/`rpc` still work.
  }

  return client as unknown;
}
